import * as React from 'react';
import * as moment from 'moment';
import { compose } from 'recompose';
import {
  StyleRulesCallback,
  Theme,
  withStyles,
  Paper,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Button,
  Grid,
  WithStyles,
  TextField,
  MenuItem,
} from '@material-ui/core';
import Autocomplete from 'react-select';

import { TreeView } from '@progress/kendo-react-treeview';
import { ScopeType, ScopeNameType } from '../ReportSettings';
import LocalizedDatePicker from '@/app/components/Pickers/LocalizedDatePicker';
import translate from '@/app/utils/translate';
import { getLegalEntities, LegalEntity } from '@/old/utils/helper';

const styles: StyleRulesCallback = (theme: Theme) => ({
  root: {
    width: 500,
  },
  tab: {
    minWidth: 0,
  },
  tabContainer: {
    padding: theme.spacing.unit * 2,
  },
  treeContainer: {
    margin: `${theme.spacing.unit * 2}px 0`,
    maxHeight: 150,
    overflow: 'auto',
    '& > .k-treeview > .k-treeview-lines': {
      paddingLeft: 0,
    },
  },
  legalEntityContainer: {
    marginTop: theme.spacing.unit * 2,
  },
  actionButtons: {
    marginTop: theme.spacing.unit * 2,
    textAlign: 'right',
    width: '100%',
  },
  yearSlider: {
    width: 250,
  },
});

enum ScreenTypeEnum {
  NONE = '',
  MYTEAM = 'myteam',
  UNIT = 'unit',
  COMPANY = 'company',
  LEGAL_ENTITY = 'legalEntity',
}

type DateRangeVariant = 'date-range' | 'years-and-end-date' | 'year-range';

type OwnProps = {
  scope: ScopeType;
  allowCompanyScope: boolean;
  allowLegalEntityScope: boolean;
  hasTeam: boolean;
  myManagedUnits: Array<object>;
  myHrUnits: Array<object>;
  dateRangeVariant?: DateRangeVariant;
  onApply: (newScope: ScopeType) => void;
  onCancel: () => void;
  onPopupLock: (shouldLock: boolean) => void;
};

type StateType = {
  scope: ScopeType;
  unitTree: Array<object>;
  legalEntities: Array<LegalEntity>;
};

type PropsType = OwnProps & WithStyles<typeof styles>;

class ScopeSelector extends React.Component<PropsType, StateType> {
  state: StateType = {
    scope: null,
    unitTree: [],
    legalEntities: [],
  };

  private concatUnits = (
    myManUnits: Array<any>,
    myHrUnits: Array<any>,
  ): Array<any> => {
    const results = myManUnits.slice();
    myHrUnits.forEach((unit: any) => {
      if (myManUnits.indexOf(unit) === -1) {
        results.push(unit);
      }
    });
    return results;
  };

  componentDidMount() {
    const { scope, myManagedUnits, myHrUnits } = this.props;
    let unitTree: Array<object> = [];
    let legalEntities: Array<LegalEntity> = [];
    if (
      (myManagedUnits && myManagedUnits.length > 0) ||
      (myHrUnits && myHrUnits.length > 0)
    ) {
      let selectedUnitId: string = null;
      if (scope && scope.type === 'unit' && !!scope.id) {
        selectedUnitId = scope.id;
      }
      const myUnits = this.concatUnits(myManagedUnits, myHrUnits);
      unitTree = this.buildTreeFromList(myUnits, selectedUnitId);
    }
    const jsonLegalEntities = getLegalEntities();
    if (jsonLegalEntities.length > 0) {
      legalEntities = jsonLegalEntities;
    }

    this.setState({ scope: scope || this.getDefaultScope(), unitTree, legalEntities });
  }

  componentDidUpdate(prevProps: PropsType) {
    const { scope, myManagedUnits, myHrUnits } = this.props;
    if (
      (myManagedUnits && myManagedUnits.length > 0) ||
      (myHrUnits && myHrUnits.length > 0)
    ) {
      if (
        !prevProps.myManagedUnits ||
        prevProps.myManagedUnits.length !== myManagedUnits.length ||
        !prevProps.myHrUnits ||
        prevProps.myHrUnits.length !== myHrUnits.length
      ) {
        let selectedUnitId: string = null;
        if (scope && scope.type === 'unit' && !!scope.id) {
          selectedUnitId = scope.id;
        }
        const myUnits = this.concatUnits(myManagedUnits, myHrUnits);
        this.setState({
          unitTree: this.buildTreeFromList(myUnits, selectedUnitId),
        });
      }
    }
  }

  buildTreeFromList = (
    data: any,
    selectedUnitId: string = null,
  ): Array<object> => {
    const SOURCE_ID_KEY = 'fTreeUnitId';
    const SOURCE_TEXT_KEY = 'fTreeUnitName';
    const SOURCE_PARENT_KEY = 'fTreeParentUnitId';
    const TARGET_ID_KEY = 'id';
    const TARGET_TEXT_KEY = 'text';
    const TARGET_CHILDREN_KEY = 'items';
    const TARGET_SELECTED_KEY = 'selected';
    const TARGET_EXPANDED_KEY = 'expanded';

    const getSelectedUnitParents = (id: string): string[] => {
      const currItem = data.find((item: any) => item[SOURCE_ID_KEY] === id);
      if (!currItem || currItem[SOURCE_PARENT_KEY] === '#') {
        return [];
      } else {
        return getSelectedUnitParents(currItem[SOURCE_PARENT_KEY]).concat(
          currItem[SOURCE_PARENT_KEY],
        );
      }
    };
    const selectedUnitParents: string[] = getSelectedUnitParents(
      selectedUnitId,
    );

    const tree: Array<object> = [];
    const childrenOf: any = {};

    for (let i = 0, length = data.length; i < length; i++) {
      const item = data[i];
      const id = item[SOURCE_ID_KEY];
      const parentId = item[SOURCE_PARENT_KEY] || '#';
      // Every item may have children
      childrenOf[id] = childrenOf[id] || [];
      // Init its children
      item[TARGET_CHILDREN_KEY] = childrenOf[id] || [];
      if (
        parentId !== '#' &&
        data.find((a: any) => a[SOURCE_ID_KEY] === parentId) !== undefined
      ) {
        childrenOf[parentId] = childrenOf[parentId] || [];
        childrenOf[parentId].push({
          [TARGET_ID_KEY]: item[SOURCE_ID_KEY],
          [TARGET_TEXT_KEY]: item[SOURCE_TEXT_KEY],
          [TARGET_CHILDREN_KEY]: item[TARGET_CHILDREN_KEY],
          [TARGET_SELECTED_KEY]: selectedUnitId === item[SOURCE_ID_KEY],
          [TARGET_EXPANDED_KEY]:
            selectedUnitParents.indexOf(item[SOURCE_ID_KEY]) !== -1,
        });
      } else {
        tree.push({
          [TARGET_ID_KEY]: item[SOURCE_ID_KEY],
          [TARGET_TEXT_KEY]: item[SOURCE_TEXT_KEY],
          [TARGET_CHILDREN_KEY]: item[TARGET_CHILDREN_KEY],
          [TARGET_SELECTED_KEY]: selectedUnitId === item[SOURCE_ID_KEY],
          [TARGET_EXPANDED_KEY]:
            selectedUnitParents.indexOf(item[SOURCE_ID_KEY]) !== -1,
        });
      }
    }

    return tree;
  };

  handleTypeChange = (_evt: any, screenType: ScreenTypeEnum) => {
    this.setState((prevState: StateType) => ({
      scope: {
        ...prevState.scope,
        type: this.getScopeTypeFromScreenType(screenType),
      },
    }));
  };

  handleDateRangeChange = (
    startDate: moment.Moment,
    endDate: moment.Moment,
  ) => {
    this.setState((prevState: StateType) => ({
      scope: {
        ...prevState.scope,
        range: {
          ...prevState.scope.range,
          startDate,
          endDate,
        },
      },
    }));
  };

  handleUnitTreeExpandChange = (event: any) => {
    const clickedUnitId = event.item.id;
    const toggleExpandedUnit = (units: any) => {
      return units.map((unit: any) => {
        if (unit.id === clickedUnitId) {
          unit.expanded = !unit.expanded;
        }
        if (unit.items && unit.items.length > 0) {
          return {
            ...unit,
            items: toggleExpandedUnit(unit.items),
          };
        } else {
          return unit;
        }
      });
    };
    this.setState({ unitTree: toggleExpandedUnit(this.state.unitTree) });
  };

  handleUnitTreeClick = (event: any) => {
    const clickedUnitId = event.item.id;
    const switchSelectedUnit = (units: any) => {
      return units.map((unit: any) => {
        unit.selected = unit.id === clickedUnitId;
        if (unit.items && unit.items.length > 0) {
          return {
            ...unit,
            items: switchSelectedUnit(unit.items),
          };
        } else {
          return unit;
        }
      });
    };
    this.setState((prevState: StateType) => ({
      unitTree: switchSelectedUnit(this.state.unitTree),
      scope: {
        ...prevState.scope,
        type: 'unit',
        id: clickedUnitId,
      },
    }));
  };

  handleApplySelection = () => {
    const newScope = Object.assign({}, this.state.scope);
    if (newScope.type === 'company') {
      newScope.id = null;
      newScope.rln = 'all';
    }
    if (newScope.type === 'manager') {
      newScope.id = null;
    }
    this.props.onApply(newScope);
  };

  handleIncludeSubunitsChanged = (includeSubunits: boolean) => {
    this.setState((prevState: StateType) => ({
      ...prevState,
      scope: {
        ...prevState.scope,
        rln: includeSubunits ? 'all' : 'direct',
      },
    }));
  };

  handleLegalEntityChanges = (legalEntityId?: number) => {
    this.setState((prevState: StateType) => ({
      ...prevState,
      scope: {
        ...prevState.scope,
        legalEntityId: legalEntityId,
      },
    }));
  };

  getScopeTypeFromScreenType = (screenType: ScreenTypeEnum): ScopeNameType => {
    switch (screenType) {
      case ScreenTypeEnum.MYTEAM:
        return 'manager';
      case ScreenTypeEnum.UNIT:
        return 'unit';
      case ScreenTypeEnum.COMPANY:
        return 'company';
      case ScreenTypeEnum.LEGAL_ENTITY:
        return 'legent';
      default:
        return '';
    }
  };

  getScreenTypeFromScopeType = (scopeType: ScopeNameType): ScreenTypeEnum => {
    switch (scopeType) {
      case 'manager':
      case 'dmgr':
      case 'amgr':
        return ScreenTypeEnum.MYTEAM;
      case 'unit':
        return ScreenTypeEnum.UNIT;
      case 'company':
        return ScreenTypeEnum.COMPANY;
      case 'legent':
        return ScreenTypeEnum.LEGAL_ENTITY;
      default:
        return ScreenTypeEnum.NONE;
    }
  };

  getDefaultScope = (): ScopeType => {
    const {
      allowCompanyScope,
        allowLegalEntityScope,
      hasTeam,
      myManagedUnits,
      myHrUnits,
    } = this.props;
    if (hasTeam) {
      return {
        type: 'manager',
        rln: 'direct',
      };
    }
    if (myManagedUnits.length > 0 || myHrUnits.length > 0) {
      return {
        type: 'unit',
        rln: 'direct',
      };
    }
    if (allowCompanyScope) {
      return {
        type: 'company',
        rln: 'all',
      };
    }
    if (allowLegalEntityScope) {
      return {
        type: 'legent',
        rln: 'direct',
      };
    }
    return {
      type: '',
      rln: 'direct',
    };
  };

  getMinAndMaxDate = (): { minDate: moment.Moment; maxDate: moment.Moment } => {
    const range = this.state.scope.range;

    const minUnitRangeDate =
      range.endDate && range.maxRange
        ? range.endDate.clone().subtract(range.maxRange, range.maxRangeUnit)
        : undefined;

    const getLaterDate = (date1: moment.Moment, date2: moment.Moment) => {
      return date1.isAfter(date2) ? date1 : date2;
    };

    const getEarlierDate = (date1: moment.Moment, date2: moment.Moment) => {
      return date1.isBefore(date2) ? date1 : date2;
    };

    const minDate =
      range.minDate && minUnitRangeDate
        ? getLaterDate(minUnitRangeDate, range.minDate)
        : minUnitRangeDate || range.minDate;

    const maxUnitRangeDate =
      range.startDate && range.maxRange
        ? range.startDate.clone().add(range.maxRange, range.maxRangeUnit)
        : undefined;

    const maxDate =
      range.maxDate && maxUnitRangeDate
        ? getEarlierDate(maxUnitRangeDate, range.maxDate)
        : maxUnitRangeDate || range.maxDate;

    return {
      minDate,
      maxDate,
    };
  };

  getDateRangeSelector = (): React.ReactElement => {
    const { classes, onPopupLock } = this.props;
    const { scope } = this.state;

    if (!scope.range) {
      return null;
    }

    const { minDate, maxDate } = this.getMinAndMaxDate();

    return (
      <Grid
        container
        className={classes.actionButtons}
        spacing={8}
        justify="flex-start"
      >
        <Grid item>
          <LocalizedDatePicker
            clearable
            label={translate.t('laStartDate')}
            value={scope.range.startDate}
            onOpen={() => onPopupLock(true)}
            onClose={() => onPopupLock(false)}
            onChange={(date: moment.Moment) =>
              this.handleDateRangeChange(date, scope.range.endDate)
            }
            maxDate={scope.range.endDate}
            minDate={minDate}
          />
        </Grid>
        <Grid item>
          <LocalizedDatePicker
            clearable
            label={translate.t('laEndDate')}
            value={scope.range.endDate}
            onOpen={() => onPopupLock(true)}
            onClose={() => onPopupLock(false)}
            onChange={(date: moment.Moment) =>
              this.handleDateRangeChange(scope.range.startDate, date)
            }
            minDate={scope.range.startDate}
            maxDate={maxDate}
          />
        </Grid>
      </Grid>
    );
  };

  getYearRangeSelector = (): React.ReactElement => {
    const { classes, onPopupLock } = this.props;
    const { scope } = this.state;

    if (!scope.range) {
      return null;
    }

    const { minDate, maxDate } = this.getMinAndMaxDate();

    return (
      <Grid
        container
        className={classes.actionButtons}
        spacing={8}
        justify="flex-start"
      >
        <Grid item>
          <LocalizedDatePicker
            clearable
            views={['year']}
            format={'YYYY'}
            label={translate.t('laStartDate')}
            value={scope.range.startDate}
            onOpen={() => onPopupLock(true)}
            onClose={() => onPopupLock(false)}
            onChange={(date: moment.Moment) =>
              this.handleDateRangeChange(
                date.startOf('year'),
                scope.range.endDate,
              )
            }
            maxDate={scope.range.endDate}
            minDate={minDate}
          />
        </Grid>
        <Grid item>
          <LocalizedDatePicker
            clearable
            views={['year']}
            format={'YYYY'}
            label={translate.t('laEndDate')}
            value={scope.range.endDate}
            onOpen={() => onPopupLock(true)}
            onClose={() => onPopupLock(false)}
            onChange={(date: moment.Moment) =>
              this.handleDateRangeChange(
                scope.range.startDate,
                date.endOf('year'),
              )
            }
            minDate={scope.range.startDate}
            maxDate={maxDate}
          />
        </Grid>
      </Grid>
    );
  };

  getYearsAndEndDateSelector = (): React.ReactElement => {
    const { classes, onPopupLock } = this.props;
    const { scope } = this.state;

    if (!scope.range) {
      return null;
    }

    const years = scope.range.endDate.diff(scope.range.startDate, 'years');
    return (
      <Grid
        container
        className={classes.actionButtons}
        spacing={8}
        justify="flex-start"
      >
        <Grid item>
          <LocalizedDatePicker
            clearable
            label={translate.t('laEndDate')}
            value={scope.range.endDate}
            onOpen={() => onPopupLock(true)}
            onClose={() => onPopupLock(false)}
            onChange={(date: moment.Moment) => {
              const startDate = date.clone().subtract(years, 'years');
              this.handleDateRangeChange(startDate, date);
            }}
          />
        </Grid>
        <Grid item>
          <TextField
            id="select-years"
            select
            label={translate.t('laYears')}
            value={years}
            onChange={(e: any) => {
              const newYears = e.target.value;
              const startDate = scope.range.endDate
                .clone()
                .subtract(newYears, 'years');
              this.handleDateRangeChange(startDate, scope.range.endDate);
            }}
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(year => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>
    );
  };

  render() {
    const {
      classes,
      allowCompanyScope,
      allowLegalEntityScope,
      hasTeam,
      dateRangeVariant = 'date-range',
      onCancel,
    } = this.props;
    const { unitTree, scope, legalEntities } = this.state;
    if (!scope) {
      return null;
    }

    const rangeFields = (rangeVariant: DateRangeVariant) => {
      switch (rangeVariant) {
        case 'years-and-end-date':
          return this.getYearsAndEndDateSelector();
        case 'year-range':
          return this.getYearRangeSelector();
        case 'date-range':
        default:
          return this.getDateRangeSelector();
      }
    };

    let screenType: string = this.getScreenTypeFromScopeType(scope.type);
    const actionButtons = (
      <Grid
        container
        className={classes.actionButtons}
        spacing={8}
        justify="flex-end"
      >
        <Grid item>
          <Button onClick={onCancel}>{translate.t('laCancel')}</Button>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            disabled={
              screenType === ScreenTypeEnum.NONE ||
              (screenType === ScreenTypeEnum.UNIT && !scope.id) ||
              (screenType === ScreenTypeEnum.LEGAL_ENTITY && !scope.legalEntityId)
            }
            onClick={this.handleApplySelection}
          >
            {translate.t('laApply')}
          </Button>
        </Grid>
      </Grid>
    );

    const availableTabConfigs: {id: ScreenTypeEnum, label: string, enabled: boolean}[] = [
      {
        id: ScreenTypeEnum.MYTEAM,
        label: translate.t('laTeam'),
        enabled: hasTeam,
      },
      {
        id: ScreenTypeEnum.UNIT,
        label: translate.t('laUnit'),
        enabled: unitTree.length > 0,
      },
      {
        id: ScreenTypeEnum.COMPANY,
        label: translate.t('laCompany'),
        enabled: allowCompanyScope,
      },
      {
        id: ScreenTypeEnum.LEGAL_ENTITY,
        label: translate.t('laLegalEntity'),
        enabled: allowLegalEntityScope,
      },
    ].filter((tab) => tab.enabled);

    const isTabAvailable = (tabId: ScreenTypeEnum | string) => {
      return availableTabConfigs.some(tab => `${tab.id}` === `${tabId}`);
    };

    const getFirstAvailableScreenType = (): ScreenTypeEnum => {
      const firstAvailableTab = availableTabConfigs[0];

      return !!firstAvailableTab ? firstAvailableTab.id : ScreenTypeEnum.NONE;
    };

    const ensureActiveTabisValid = (tabId: ScreenTypeEnum | string): string => {
      return isTabAvailable(tabId) ? tabId : getFirstAvailableScreenType();
    };

    screenType = ensureActiveTabisValid(screenType);

    if (screenType === ScreenTypeEnum.NONE) {
      return null;
    }

    const tabs = (
      <Tabs
        variant="fullWidth"
        value={screenType}
        onChange={this.handleTypeChange}
      >
        {availableTabConfigs.map((tab, index) => {
          return (
              <Tab
                key={`tab-${index}-${tab.id}`}
                label={tab.label}
                value={tab.id}
                className={classes.tab}
              />
          );
        })}
      </Tabs>
    );

    return (
      <Paper className={classes.root}>
        {tabs}
        {screenType === ScreenTypeEnum.MYTEAM && (
          <Paper className={classes.tabContainer}>
            <FormHelperText>
              {translate.t('label_report_scope_myteam_info')}
            </FormHelperText>
            {rangeFields(dateRangeVariant)}
            {actionButtons}
          </Paper>
        )}
        {screenType === ScreenTypeEnum.UNIT && (
          <Paper className={classes.tabContainer}>
            <FormHelperText>
              {translate.t('label_report_scope_unit_info')}
            </FormHelperText>
            <Paper elevation={0} className={classes.treeContainer}>
              <TreeView
                data={unitTree}
                expandIcons={true}
                onExpandChange={this.handleUnitTreeExpandChange}
                onItemClick={this.handleUnitTreeClick}
              />
            </Paper>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={scope.rln === 'all'}
                  onChange={(
                    _evt: React.ChangeEvent<HTMLInputElement>,
                    checked: boolean,
                  ) => this.handleIncludeSubunitsChanged(checked)}
                />
              }
              label={translate.t('label_include_subunits')}
            />
            {rangeFields(dateRangeVariant)}
            {actionButtons}
          </Paper>
        )}
        {screenType === ScreenTypeEnum.COMPANY && (
          <Paper className={classes.tabContainer}>
            <FormHelperText>
              {translate.t('label_report_scope_company_info')}
            </FormHelperText>
            {rangeFields(dateRangeVariant)}
            {actionButtons}
          </Paper>
        )}
        {screenType === ScreenTypeEnum.LEGAL_ENTITY && (
          <Paper className={classes.tabContainer}>
            <FormHelperText>
              {translate.t('label_report_scope_legal_entity_info')}
            </FormHelperText>
            <div className={classes.legalEntityContainer}>
              <Autocomplete
                fullwidth={true}
                options={legalEntities}
                getOptionLabel={(option: LegalEntity) => option.fLegalEntityName}
                getOptionValue={(option: LegalEntity) => `${option.fLegalEntityId}`}
                value={legalEntities.find((le) => le.fLegalEntityId === scope.legalEntityId) || null}
                onChange={(value: LegalEntity | null) => {
                  this.handleLegalEntityChanges(!!value ? value.fLegalEntityId : undefined);
                }}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 12000 }) }}
              />
            </div>
            {rangeFields(dateRangeVariant)}
            {actionButtons}
          </Paper>
        )}
      </Paper>
    );
  }
}

const enhance = compose<any, OwnProps>(withStyles(styles, { withTheme: true }));

export default enhance(ScopeSelector);
